'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Eye, EyeOff, Loader2, Gift, ArrowRight, ShieldAlert } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { authApi } from '@/lib/api';
import { FREE_TRIAL_BALANCE, FREE_TRIAL_REQUESTS } from '@/lib/pricing';

const Turnstile = dynamic(
  () => import('@/components/ui/Turnstile').then((mod) => mod.Turnstile),
  {
    ssr: false,
    loading: () => (
      <div className="flex min-h-[65px] items-center justify-center text-sm text-zinc-500">
        Loading security check...
      </div>
    ),
  }
);

type Tab = 'login' | 'signup' | 'admin';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

interface AdminFormData {
  email: string;
  password: string;
}

export default function AuthForm() {
  const [activeTab, setActiveTab] = useState<Tab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginTurnstileToken, setLoginTurnstileToken] = useState('');
  const [signupTurnstileToken, setSignupTurnstileToken] = useState('');
  const [adminTurnstileToken, setAdminTurnstileToken] = useState('');
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });
  const signupForm = useForm<SignupFormData>({
    defaultValues: { email: '', password: '', confirmPassword: '' },
  });
  const adminForm = useForm<AdminFormData>({ defaultValues: { email: '', password: '' } });

  const handleLoginSubmit = async (data: LoginFormData) => {
    if (!loginTurnstileToken) {
      toast.error('Please complete the captcha verification.');
      return;
    }
    try {
      const res = (await authApi.login(data.email, data.password, loginTurnstileToken)) as { data: { token: string } };
      localStorage.setItem('userToken', res.data.token);
      toast.success('Signed in successfully', {
        description: 'Welcome back to AR Digital Services',
      });
      router.push('/user-dashboard');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid credentials';
      toast.error(msg);
      setLoginTurnstileToken('');
    }
  };

  const handleSignupSubmit = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    if (!signupTurnstileToken) {
      toast.error('Please complete the captcha verification.');
      return;
    }
    try {
      const res = (await authApi.signup(data.email, data.password, signupTurnstileToken)) as { data: { token: string } };
      localStorage.setItem('userToken', res.data.token);
      toast.success(`Account created! 100 free monthly requests added`, {
        description: `Your first 100 requests are ready to use immediately`,
      });
      router.push('/user-dashboard');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Signup failed';
      toast.error(msg);
      setSignupTurnstileToken('');
    }
  };

  const handleAdminSubmit = async (data: AdminFormData) => {
    if (!adminTurnstileToken) {
      toast.error('Please complete the captcha verification.');
      return;
    }
    try {
      const res = (await authApi.adminLogin(data.email, data.password, adminTurnstileToken)) as {
        data: { token: string };
      };
      localStorage.setItem('adminToken', res.data.token);
      toast.success('Admin login successful');
      router.push('/admin');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid admin credentials';
      toast.error(msg);
      setAdminTurnstileToken(''); // Reset turnstile on error
    }
  };

  return (
    <div className="w-full max-w-[420px]">
      {/* Mobile logo */}
      <div className="flex items-center gap-2.5 mb-8 lg:hidden">
        <AppLogo size={36} />
        <span className="font-semibold text-lg text-zinc-100">AR Digital Services</span>
      </div>

      {/* Tab switcher */}
      <div className="flex bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-1 mb-6">
        {(['login', 'signup', 'admin'] as Tab[]).map((tab) => (
          <button
            key={`tab-${tab}`}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 ${
              activeTab === tab
                ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm border border-zinc-200 dark:border-zinc-600'
                : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
            }`}
          >
            {tab === 'login' ? 'Sign In' : tab === 'signup' ? 'Create Account' : 'Admin'}
          </button>
        ))}
      </div>

      {/* Heading */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          {activeTab === 'login'
            ? 'Welcome back'
            : activeTab === 'signup'
              ? 'Get started free'
              : 'Admin Access'}
        </h2>
        <p className="text-sm text-zinc-500 dark:text-zinc-500 mt-1">
          {activeTab === 'login'
            ? 'Sign in to your developer account'
            : activeTab === 'signup'
              ? `Create your account and get 100 free requests every month`
              : 'Sign in to the admin control panel'}
        </p>
        {activeTab === 'signup' && (
          <div className="flex items-center gap-2 mt-3 bg-blue-500/5 dark:bg-emerald-500/5 border border-blue-500/20 dark:border-emerald-500/20 rounded-lg px-3 py-2">
            <Gift size={14} className="text-blue-600 dark:text-emerald-400 shrink-0" />
            <p className="text-xs text-blue-600 dark:text-emerald-400 font-medium">
              100 free monthly requests - no card required, start building instantly
            </p>
          </div>
        )}
        {activeTab === 'admin' && (
          <div className="flex items-center gap-2 mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
            <ShieldAlert size={14} className="text-amber-400 shrink-0" />
            <p className="text-xs text-amber-400 font-medium">
              Admin credentials required — restricted access
            </p>
          </div>
        )}
      </div>

      {/* Login Form */}
      {activeTab === 'login' && (
        <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="login-email" className="form-label">
              Email address
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              className={`form-input ${loginForm.formState.errors.email ? 'border-red-500/50 focus:ring-red-500/30' : ''}`}
              placeholder="you@company.com"
              {...loginForm.register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {loginForm.formState.errors.email && (
              <p className="text-xs text-red-400 mt-1.5" role="alert">
                {loginForm.formState.errors.email.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="login-password" className="form-label">
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={`form-input pr-10 ${loginForm.formState.errors.password ? 'border-red-500/50 focus:ring-red-500/30' : ''}`}
                placeholder="Enter your password"
                {...loginForm.register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="text-xs text-red-400 mt-1.5" role="alert">
                {loginForm.formState.errors.password.message}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              id="remember"
              type="checkbox"
              className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-cyan-500 focus:ring-cyan-500/30"
              {...loginForm.register('remember')}
            />
            <label htmlFor="remember" className="text-sm text-zinc-400 cursor-pointer">
              Remember me for 30 days
            </label>
          </div>

          {/* Turnstile Captcha */}
          <div className="flex justify-center">
            <Turnstile
              siteKey="0x4AAAAAACG0B7jzIiua8JFj"
              onVerify={(token) => setLoginTurnstileToken(token)}
              onError={() => toast.error('Captcha verification failed. Please try again.')}
              onExpire={() => setLoginTurnstileToken('')}
              theme="auto"
            />
          </div>

          <button
            type="submit"
            disabled={loginForm.formState.isSubmitting}
            className="w-full btn-primary flex items-center justify-center gap-2 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loginForm.formState.isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <span>Sign In</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      )}

      {/* Signup Form */}
      {activeTab === 'signup' && (
        <form
          onSubmit={signupForm.handleSubmit(handleSignupSubmit)}
          className="space-y-4"
          noValidate
        >
          <div>
            <label htmlFor="signup-email" className="form-label">
              Email address
            </label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              className={`form-input ${signupForm.formState.errors.email ? 'border-red-500/50 focus:ring-red-500/30' : ''}`}
              placeholder="you@company.com"
              {...signupForm.register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {signupForm.formState.errors.email && (
              <p className="text-xs text-red-400 mt-1.5" role="alert">
                {signupForm.formState.errors.email.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="signup-password" className="form-label">
              Password
            </label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={`form-input pr-10 ${signupForm.formState.errors.password ? 'border-red-500/50 focus:ring-red-500/30' : ''}`}
                placeholder="Create a password"
                {...signupForm.register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signupForm.formState.errors.password && (
              <p className="text-xs text-red-400 mt-1.5" role="alert">
                {signupForm.formState.errors.password.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="signup-confirm" className="form-label">
              Confirm password
            </label>
            <div className="relative">
              <input
                id="signup-confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={`form-input pr-10 ${signupForm.formState.errors.confirmPassword ? 'border-red-500/50 focus:ring-red-500/30' : ''}`}
                placeholder="Confirm your password"
                {...signupForm.register('confirmPassword', {
                  required: 'Please confirm your password',
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {signupForm.formState.errors.confirmPassword && (
              <p className="text-xs text-red-400 mt-1.5" role="alert">
                {signupForm.formState.errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Turnstile Captcha */}
          <div className="flex justify-center">
            <Turnstile
              siteKey="0x4AAAAAACG0B7jzIiua8JFj"
              onVerify={(token) => setSignupTurnstileToken(token)}
              onError={() => toast.error('Captcha verification failed. Please try again.')}
              onExpire={() => setSignupTurnstileToken('')}
              theme="auto"
            />
          </div>

          <button
            type="submit"
            disabled={signupForm.formState.isSubmitting}
            className="w-full btn-primary flex items-center justify-center gap-2 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {signupForm.formState.isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Creating account...</span>
              </>
            ) : (
              <>
                <span>Create Account</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>
      )}

      {/* Admin Form */}
      {activeTab === 'admin' && (
        <form onSubmit={adminForm.handleSubmit(handleAdminSubmit)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="admin-email" className="form-label">
              Admin email
            </label>
            <input
              id="admin-email"
              type="email"
              autoComplete="email"
              className={`form-input ${adminForm.formState.errors.email ? 'border-red-500/50 focus:ring-red-500/30' : ''}`}
              placeholder="admin@example.com"
              {...adminForm.register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            {adminForm.formState.errors.email && (
              <p className="text-xs text-red-400 mt-1.5" role="alert">
                {adminForm.formState.errors.email.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="admin-password" className="form-label">
              Admin password
            </label>
            <div className="relative">
              <input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={`form-input pr-10 ${adminForm.formState.errors.password ? 'border-red-500/50 focus:ring-red-500/30' : ''}`}
                placeholder="Enter admin password"
                {...adminForm.register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {adminForm.formState.errors.password && (
              <p className="text-xs text-red-400 mt-1.5" role="alert">
                {adminForm.formState.errors.password.message}
              </p>
            )}
          </div>

          {/* Turnstile Captcha */}
          <div className="flex justify-center">
            <Turnstile
              siteKey="0x4AAAAAACG0B7jzIiua8JFj"
              onVerify={(token) => setAdminTurnstileToken(token)}
              onError={() => toast.error('Captcha verification failed. Please try again.')}
              onExpire={() => setAdminTurnstileToken('')}
              theme="auto"
            />
          </div>

          <button
            type="submit"
            disabled={adminForm.formState.isSubmitting}
            className="w-full bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 font-semibold px-4 py-3 rounded-lg transition-all duration-150 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {adminForm.formState.isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Authenticating...</span>
              </>
            ) : (
              <>
                <ShieldAlert size={16} />
                <span>Admin Sign In</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
